import { useState } from 'react';
import { Box, Typography } from '@mui/material';

import { COLORS } from '../../../IndividualPlan/tokens';
import { gradeName, gradeShortLabel } from '../../listKeys';
import type { PeHistoryYear, ScoreUnit } from '../../types';
import { fmtLevel, fmtScale, shortYear } from '../../utils/drilldownFormat';
import { ScaleResetMarker } from './ScaleResetMarker';

const H = 240;
const LABEL_BAND = 20;
const PALETTE = [
  COLORS.navy,
  COLORS.orange,
  COLORS.blue,
  '#2E7D32',
  '#6A1B9A',
  '#AD1457',
  '#00695C',
];

type Series = Map<number, Map<number, number>>;

function seriesOf(history: PeHistoryYear[], unit: ScoreUnit): { series: Series; grades: number[] } {
  const series: Series = new Map();
  for (const year of history) {
    for (const g of year.grades) {
      if (!series.has(g.grade)) series.set(g.grade, new Map());
      series.get(g.grade)?.set(year.end_year, unit === 'scale_score' ? g.scale : g.level);
    }
  }
  const grades = Array.from(series.keys()).sort((a, b) => a - b);
  return { series, grades };
}

function defaultSelection(grades: number[]): number[] {
  if (grades.length <= 3) return grades;
  const picks = [0, Math.floor((grades.length - 1) / 2), grades.length - 1];
  return picks.map((i) => grades[i] as number);
}

function pathFor(
  years: number[],
  values: Map<number, number> | undefined,
  x: (i: number) => number,
  y: (v: number) => number,
): { d: string; last: [number, number] | null } {
  let d = '';
  let pen = false;
  let last: [number, number] | null = null;
  years.forEach((year, i) => {
    const v = values?.get(year);
    if (v == null) {
      pen = false;
      return;
    }
    const point: [number, number] = [x(i), y(v)];
    d += `${pen ? 'L' : 'M'}${point[0].toFixed(1)} ${point[1].toFixed(1)}`;
    pen = true;
    last = point;
  });
  return { d, last };
}

function Panel({
  testId,
  years,
  series,
  grades,
  on,
  colorOf,
  unit,
  width,
  threshold,
  dots,
  labels,
}: {
  testId: string;
  years: number[];
  labels: string[];
  series: Series;
  grades: number[];
  on: Set<number>;
  colorOf: (g: number) => string;
  unit: ScoreUnit;
  width: number;
  threshold: number | null;
  dots: boolean;
}) {
  const lo = unit === 'scale_score' ? 100 : 1;
  const hi = unit === 'scale_score' ? 600 : 6;
  const pad = 40;
  const x = (i: number) =>
    years.length <= 1 ? width / 2 : pad + i * ((width - 2 * pad) / (years.length - 1));
  const y = (v: number) => H - ((v - lo) / (hi - lo)) * H;
  const ticks = unit === 'scale_score' ? [600, 500, 400, 300, 200, 100] : [6, 5, 4, 3, 2, 1];
  return (
    <Box
      data-testid={testId}
      sx={{ display: 'flex', gap: 1, flex: `${years.length} 1 0`, minWidth: 0 }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: H,
          fontSize: 11,
          color: COLORS.muted,
        }}
      >
        {ticks.map((t) => (
          <span key={t}>{t}</span>
        ))}
      </Box>
      <svg
        viewBox={`0 0 ${width} ${H + LABEL_BAND}`}
        width="100%"
        style={{ height: 'auto', maxHeight: H + LABEL_BAND, display: 'block' }}
        role="img"
        aria-label="Score history by grade"
      >
        {labels.map((label, i) => (
          <text
            key={label}
            data-testid="year-label"
            x={x(i)}
            y={H + LABEL_BAND - 4}
            textAnchor="middle"
            fontSize={12}
            fill={COLORS.muted}
          >
            {label}
          </text>
        ))}
        {ticks.map((t) => (
          <line key={t} x1={0} x2={width} y1={y(t)} y2={y(t)} stroke={COLORS.hairlineSoft} />
        ))}
        {threshold != null && unit !== 'scale_score' && (
          <line
            data-testid="exit-cut"
            x1={0}
            x2={width}
            y1={y(threshold)}
            y2={y(threshold)}
            stroke={COLORS.muted}
            strokeDasharray="6 4"
          />
        )}
        {grades
          .filter((g) => !on.has(g))
          .map((g) => (
            <path
              key={g}
              d={pathFor(years, series.get(g), x, y).d}
              fill="none"
              stroke={COLORS.hairline}
              strokeWidth={2}
            />
          ))}
        {grades
          .filter((g) => on.has(g))
          .map((g) => {
            const p = pathFor(years, series.get(g), x, y);
            return (
              <g key={g}>
                <path d={p.d} fill="none" stroke={colorOf(g)} strokeWidth={3} />
                {dots && p.last && <circle cx={p.last[0]} cy={p.last[1]} r={5} fill={colorOf(g)} />}
                {years.length === 1 && p.last && (
                  <circle cx={p.last[0]} cy={p.last[1]} r={6} fill={colorOf(g)} />
                )}
              </g>
            );
          })}
      </svg>
    </Box>
  );
}

export function GradeHistoryChart({
  history,
  unit,
  threshold,
}: {
  history: PeHistoryYear[];
  unit: ScoreUnit;
  threshold: number | null;
}) {
  const { series, grades } = seriesOf(history, unit);
  const [selected, setSelected] = useState<number[] | null>(null);
  if (history.length === 0 || grades.length === 0) return null;
  const on = new Set(selected ?? defaultSelection(grades));
  const toggle = (g: number) =>
    setSelected(on.has(g) ? Array.from(on).filter((x) => x !== g) : [...on, g]);
  const colorOf = (g: number) => PALETTE[grades.indexOf(g) % PALETTE.length] as string;
  const prior = history.filter((y) => y.reset_era !== 'post_reset').map((y) => y.end_year);
  const current = history.filter((y) => y.reset_era === 'post_reset').map((y) => y.end_year);
  const spansReset = prior.length > 0 && current.length > 0;
  const latest = history[history.length - 1] as PeHistoryYear;
  const fmt = (v: number | null | undefined) =>
    unit === 'scale_score' ? fmtScale(v) : fmtLevel(v);
  const unitLine =
    unit === 'scale_score'
      ? 'Average composite scale score, 100 to 600 (no exit line: the exit cut is a proficiency level, not a scale score)'
      : 'Average composite level, 1 to 6';
  const caption = spansReset
    ? `${unitLine}. The left panel is the prior ACCESS scale; the right is the new scale from ${shortYear(history.find((y) => y.reset_era === 'post_reset')?.school_year ?? '')}, which is not comparable with it. Grey lines are the grades not selected. Each line is whoever was in that grade that year. The new scale’s exit cut is set at WIDA’s July 2026 standard setting.`
    : `${unitLine}. Grey lines are the grades not selected. Each line is whoever was in that grade that year: different students each year, not one group followed over time.`;

  return (
    <Box
      data-testid="grade-history-chart"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        p: 2,
        bgcolor: COLORS.bg,
        borderRadius: '8px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography component="p" sx={{ m: 0, fontSize: 16, fontWeight: 700, color: COLORS.ink }}>
          Score history by grade
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            flexWrap: 'wrap',
          }}
        >
          <Typography component="span" sx={{ fontSize: 12, color: COLORS.muted, mr: 0.5 }}>
            Show grades
          </Typography>
          {grades.map((g) => (
            <Box
              key={g}
              component="button"
              type="button"
              aria-pressed={on.has(g)}
              onClick={() => toggle(g)}
              sx={{
                border: `1px solid ${COLORS.divider}`,
                borderRadius: '999px',
                px: 1.25,
                py: 0.25,
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
                bgcolor: on.has(g) ? colorOf(g) : '#FFFFFF',
                color: on.has(g) ? '#FFFFFF' : COLORS.muted,
              }}
            >
              {gradeShortLabel(g)}
            </Box>
          ))}
        </Box>
      </Box>
      {spansReset ? (
        <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1 }}>
          <Panel
            testId="grade-history-prior"
            years={prior}
            labels={history.filter((y) => prior.includes(y.end_year)).map((y) => shortYear(y.school_year))}
            series={series}
            grades={grades}
            on={on}
            colorOf={colorOf}
            unit={unit}
            width={520}
            threshold={threshold}
            dots={false}
          />
          <ScaleResetMarker />
          <Panel
            testId="grade-history-current"
            years={current}
            labels={history.filter((y) => current.includes(y.end_year)).map((y) => shortYear(y.school_year))}
            series={series}
            grades={grades}
            on={on}
            colorOf={colorOf}
            unit={unit}
            width={148}
            threshold={null}
            dots
          />
        </Box>
      ) : (
        <Panel
          testId="grade-history-prior"
          years={history.map((y) => y.end_year)}
          labels={history.map((y) => shortYear(y.school_year))}
          series={series}
          grades={grades}
          on={on}
          colorOf={colorOf}
          unit={unit}
          width={800}
          threshold={threshold}
          dots
        />
      )}
      <Box
        data-testid="grade-history-legend"
        sx={{
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
          fontSize: 12,
          fontWeight: 600,
          color: COLORS.ink,
        }}
      >
        {grades
          .filter((g) => on.has(g))
          .map((g) => (
            <Box
              key={g}
              component="span"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            >
              <Box
                component="i"
                sx={{
                  width: 12,
                  height: 3,
                  bgcolor: colorOf(g),
                  display: 'inline-block',
                }}
              />
              {`${gradeName(g)} · ${fmt(latest.grades.find((row) => row.grade === g)?.level != null ? (unit === 'scale_score' ? latest.grades.find((row) => row.grade === g)?.scale : latest.grades.find((row) => row.grade === g)?.level) : null)}`}
            </Box>
          ))}
        {threshold != null && unit !== 'scale_score' && (
          <Typography component="span" sx={{ fontSize: 12, fontWeight: 500, color: COLORS.muted }}>
            {`Exit target ${fmtLevel(threshold)}${spansReset ? ' · prior scale' : ''}`}
          </Typography>
        )}
      </Box>
      <Typography
        component="p"
        data-testid="grade-history-caption"
        sx={{ m: 0, fontSize: 12, fontWeight: 500, color: COLORS.muted }}
      >
        {caption}
      </Typography>
    </Box>
  );
}
